import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, Download, Eye, Save, XCircle } from 'react-feather';
import { GridField } from './gridTypes';

interface GridFooterProps {
    page: number;
    pageInput: number;
    totalPages: number;
    total: number;
    pageSize: number | 'auto';
    fields: GridField[];
    visibleColumns: Record<string, boolean>;
    showColumnSelector: boolean;
    hasSavedState: boolean;
    setPage: (page: number) => void;
    setPageInput: (page: number) => void;
    setPageSize: (size: number | 'auto') => void;
    setVisibleColumns: (columns: Record<string, boolean>) => void;
    setShowColumnSelector: (show: boolean) => void;
    refreshData: () => void;
    saveState: () => void;
    clearSavedState: () => void;
}

export function GridFooter({
    page,
    pageInput,
    totalPages,
    total,
    pageSize,
    fields,
    visibleColumns,
    showColumnSelector,
    hasSavedState,
    setPage,
    setPageInput,
    setPageSize,
    setVisibleColumns,
    setShowColumnSelector,
    refreshData,
    saveState,
    clearSavedState
}: GridFooterProps) {
    return (
        <div className="grid-footer">
            <div className="grid-footer-row grid-footer-row-nav">
                <button
                    type="button"
                    className="grid-icon-btn"
                    onClick={() => { setPage(1); setPageInput(1); }}
                    disabled={page === 1}
                    title="Primeira"
                >
                    <ChevronsLeft size={13} />
                </button>
                <button
                    type="button"
                    className="grid-icon-btn"
                    onClick={() => { const p = Math.max(1, page - 1); setPage(p); setPageInput(p); }}
                    disabled={page === 1}
                    title="Anterior"
                >
                    <ChevronLeft size={13} />
                </button>
                <span className="grid-footer-text">
                    Página:
                    <input
                        type="number"
                        className="grid-page-input"
                        value={pageInput}
                        onChange={(e) => {
                            const v = parseInt(e.target.value);
                            if (v >= 1 && v <= totalPages) setPageInput(v);
                        }}
                        min={1}
                        max={totalPages}
                    />
                    de {totalPages}
                </span>
                <button
                    type="button"
                    className="grid-icon-btn"
                    onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); setPageInput(p); }}
                    disabled={page >= totalPages}
                    title="Próxima"
                >
                    <ChevronRight size={13} />
                </button>
                <button
                    type="button"
                    className="grid-icon-btn"
                    onClick={() => { setPage(totalPages); setPageInput(totalPages); }}
                    disabled={page >= totalPages}
                    title="Última"
                >
                    <ChevronsRight size={13} />
                </button>
                <button type="button" className="grid-icon-btn" onClick={refreshData} title="Atualizar">
                    <RefreshCw size={13} />
                </button>
                <button type="button" className="grid-icon-btn" title="Exportar">
                    <Download size={13} />
                </button>
            </div>

            <div className="grid-footer-row grid-footer-row-tools">
                <div className="grid-column-selector">
                    <button
                        type="button"
                        className="grid-icon-btn"
                        onClick={() => setShowColumnSelector(!showColumnSelector)}
                        title="Colunas"
                    >
                        <Eye size={13} />
                    </button>
                    {showColumnSelector && (
                        <div className="grid-column-dropdown">
                            {fields.map(field => (
                                <label key={field.name} className="grid-column-option">
                                    <input
                                        type="checkbox"
                                        checked={visibleColumns[field.name] !== false}
                                        onChange={(e) => setVisibleColumns({ ...visibleColumns, [field.name]: e.target.checked })}
                                    />
                                    {field.header}
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                <div className="grid-footer-tools-scroll">
                    <button
                        type="button"
                        className={`grid-icon-btn ${hasSavedState ? 'grid-icon-btn-active' : ''}`}
                        onClick={saveState}
                        title="Salvar estado"
                    >
                        <Save size={13} />
                    </button>
                    {hasSavedState && (
                        <button
                            type="button"
                            className="grid-icon-btn grid-icon-btn-danger"
                            onClick={clearSavedState}
                            title="Limpar estado salvo"
                        >
                            <XCircle size={13} />
                        </button>
                    )}
                    <span className="grid-footer-text">
                        <span className="grid-footer-label-full">Registros por Página:</span>
                        <span className="grid-footer-label-short">Reg/Pág:</span>
                        <select
                            className="grid-pagesize-select"
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(e.target.value === 'auto' ? 'auto' : parseInt(e.target.value));
                                setPage(1);
                                setPageInput(1);
                            }}
                        >
                            <option value="auto">Auto</option>
                            <option value="15">15</option>
                            <option value="30">30</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </span>
                    <span className="grid-footer-total">
                        <span className="grid-footer-label-full">Total de Registro(s):</span>
                        <span className="grid-footer-label-short">Total:</span>
                        <strong>{total.toLocaleString('pt-BR')}</strong>
                    </span>
                </div>
            </div>
        </div>
    );
}
